/**
 * Vehicle PIN change page.
 *
 * Mounted at /vehicles/change-pin/:token. Token is in the URL path. No
 * login required. Backend is single-use: a successful change consumes the
 * token; further visits return 410.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { loadChangePinContext, changePin } from '../../api/vehiclePinApi.js';
import GreenChargeLogo from '../Common/GreenChargeLogo.jsx';
import './ChangePinFlow.less';

function errMessage(err, fallback = 'Something went wrong') {
  return (
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

function TerminalScreen({ title, message, kind = 'info' }) {
  return (
    <div className={`cp-shell cp-shell--terminal cp-shell--${kind}`}>
      <div className="cp-container">
        <div className="cp-brandbar"><GreenChargeLogo variant="wordmark" /></div>
        <div className="cp-card cp-card--centered">
          <h1>{title}</h1>
          <p>{message}</p>
        </div>
      </div>
    </div>
  );
}

function ChangePinFlow() {
  const { token: rawToken } = useParams();
  const token = decodeURIComponent(rawToken || '');

  const [phase, setPhase] = useState('loading'); // loading | form | done | terminal
  const [terminal, setTerminal] = useState(null);
  const [context, setContext] = useState(null);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const pinRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    loadChangePinContext(token)
      .then((data) => {
        if (cancelled) return;
        if (data?.ok) {
          setContext(data);
          setPhase('form');
          // Focus the first input after the next paint.
          setTimeout(() => pinRef.current?.focus(), 0);
        } else {
          setTerminal(mapToTerminal(data?.error));
          setPhase('terminal');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const errKey = err?.response?.data?.error || 'invalid_token';
        const t = mapToTerminal(errKey);
        if (!t) {
          setTerminal({
            title: 'Could not load link',
            message: errMessage(err, 'Please retry in a moment.'),
            kind: 'warn',
          });
        } else setTerminal(t);
        setPhase('terminal');
      });
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();
    setSubmitError(null);
    if (!/^\d{4}$/.test(pin)) {
      setSubmitError('PIN must be exactly 4 digits.');
      return;
    }
    if (pin !== confirmPin) {
      setSubmitError('PINs do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const data = await changePin(token, { pin, confirmPin });
      if (data?.ok) {
        setPhase('done');
      } else {
        const t = mapToTerminal(data?.error);
        if (t) {
          setTerminal(t);
          setPhase('terminal');
        } else {
          setSubmitError(data?.message || 'Could not change PIN.');
          setSubmitting(false);
        }
      }
    } catch (err) {
      const errKey = err?.response?.data?.error;
      const t = mapToTerminal(errKey);
      if (t && (errKey === 'token_already_used' || errKey === 'token_expired' || errKey === 'token_not_found_or_used_or_expired' || errKey === 'vehicle_unavailable')) {
        setTerminal(t);
        setPhase('terminal');
      } else {
        setSubmitError(errMessage(err, 'Could not change PIN.'));
        setSubmitting(false);
      }
    }
  }, [token, pin, confirmPin]);

  if (phase === 'loading') {
    return (
      <div className="cp-shell">
        <div className="cp-container">
          <div className="cp-brandbar"><GreenChargeLogo variant="wordmark" /></div>
          <div className="cp-card">Loading…</div>
        </div>
      </div>
    );
  }

  if (phase === 'terminal') {
    return <TerminalScreen {...terminal} />;
  }

  if (phase === 'done') {
    return (
      <TerminalScreen
        kind="success"
        title="PIN updated"
        message="Your new PIN is now active. You can use it at any GreenCharge station."
      />
    );
  }

  return (
    <div className="cp-shell">
      <div className="cp-container">
        <div className="cp-brandbar"><GreenChargeLogo variant="wordmark" /></div>

        <header className="cp-header">
          <h1>Change your PIN</h1>
          {context?.vehiclePlate && (
            <p className="cp-subtle">
              for vehicle <strong>{context.vehiclePlate}</strong>
              {context.customerName ? <> · {context.customerName}</> : null}
            </p>
          )}
        </header>

        <form className="cp-card" onSubmit={handleSubmit} noValidate>
          <p className="cp-subtle">
            Pick a 4-digit PIN you'll use at the charger. Don't share it.
          </p>

          <label className="cp-field">
            <span className="cp-field__label">New PIN</span>
            <input
              ref={pinRef}
              className="cp-field__input"
              type="tel"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={4}
              pattern="\d{4}"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="••••"
              required
            />
          </label>

          <label className="cp-field">
            <span className="cp-field__label">Confirm PIN</span>
            <input
              className="cp-field__input"
              type="tel"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={4}
              pattern="\d{4}"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="••••"
              required
            />
          </label>

          {submitError && <p className="cp-alert cp-alert--danger">{submitError}</p>}

          <button
            type="submit"
            className="cp-btn cp-btn--primary cp-btn--large"
            disabled={submitting || pin.length !== 4 || confirmPin.length !== 4}
          >
            {submitting ? 'Saving…' : 'Save new PIN'}
          </button>

          {context?.expiresAt && (
            <p className="cp-subtle cp-subtle--small">
              This link expires on {new Date(context.expiresAt).toLocaleDateString()}.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

function mapToTerminal(key) {
  switch (key) {
    case 'token_expired':
      return { kind: 'warn', title: 'Link expired', message: 'Ask your administrator to send a new Change-PIN link.' };
    case 'token_already_used':
      return { kind: 'warn', title: 'Link already used', message: 'This link has already been used to change a PIN. Ask your administrator for a new one if you need to change it again.' };
    case 'vehicle_unavailable':
      return { kind: 'warn', title: 'Vehicle unavailable', message: "This vehicle isn't currently active. Please contact your administrator." };
    case 'token_not_found_or_used_or_expired':
      return { kind: 'warn', title: 'Link no longer valid', message: 'This link cannot be used. It may have expired or already been used. Ask your administrator for a new one.' };
    case 'invalid_token':
      return { kind: 'warn', title: 'Invalid link', message: 'This link is not valid. Make sure you used the full link from your email.' };
    default:
      return null;
  }
}

export default ChangePinFlow;
